import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Skeleton,
  Alert,
  Chip,
  Tooltip as MuiTooltip
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine
} from 'recharts';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RemoveIcon from '@mui/icons-material/Remove';
import { getIndividualTendencies } from '../../utils/analyticsApi';

const IndividualTendencies = ({ sessionId, scope, organizationId, teamId }) => {
  const [tendencies, setTendencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTendencies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, scope, organizationId, teamId]);

  const fetchTendencies = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (sessionId) params.sessionId = sessionId;
      if (organizationId) params.organizationId = organizationId;
      if (teamId) params.teamId = teamId;
      const response = await getIndividualTendencies(params);
      setTendencies(response.data.tendencies || []);
    } catch (err) {
      console.error('Error fetching individual tendencies:', err);
      setError('Failed to load individual voting tendencies');
    } finally {
      setLoading(false);
    }
  };

  const getBarColor = (score) => {
    if (score > 0.2) return '#EF4444'; // Red - votes higher
    if (score < -0.2) return '#3B82F6'; // Blue - votes lower
    return '#10B981'; // Green - balanced
  };

  const getTendencyLabel = (score) => {
    if (score > 0.2) return 'Votes Higher';
    if (score < -0.2) return 'Votes Lower';
    return 'Balanced';
  };

  const getTendencyIcon = (score) => {
    if (score > 0.2) return <ArrowUpwardIcon fontSize="small" sx={{ color: '#EF4444' }} />;
    if (score < -0.2) return <ArrowDownwardIcon fontSize="small" sx={{ color: '#3B82F6' }} />;
    return <RemoveIcon fontSize="small" sx={{ color: '#10B981' }} />;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Paper sx={{ p: 1.5 }}>
          <Typography variant="subtitle2">{data.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            Tendency: {getTendencyLabel(data.tendencyScore)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Total Votes: {data.totalVotes}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Exact Match: {data.exactMatchRate}%
          </Typography>
          <Box sx={{ mt: 1, fontSize: '0.75rem' }}>
            <span style={{ color: '#EF4444' }}>Higher: {data.votedHigherRate}%</span>
            {' | '}
            <span style={{ color: '#3B82F6' }}>Lower: {data.votedLowerRate}%</span>
          </Box>
        </Paper>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Individual Voting Tendencies
        </Typography>
        <Skeleton variant="rectangular" height={300} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (tendencies.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body1" color="text.secondary">
          No individual voting data available yet
        </Typography>
      </Box>
    );
  }

  // Filter to participants with at least 3 votes
  const significantTendencies = tendencies.filter(t => t.totalVotes >= 3);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Individual Voting Tendencies
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        How individual team members tend to vote relative to final estimates
      </Typography>

      {significantTendencies.length > 0 ? (
        <>
          <Paper sx={{ p: 2 }}>
            <ResponsiveContainer width="100%" height={Math.max(200, significantTendencies.length * 40)}>
              <BarChart
                data={significantTendencies}
                layout="vertical"
                margin={{ top: 10, right: 30, left: 80, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                <XAxis
                  type="number"
                  domain={[-1, 1]}
                  tick={{ fill: '#64748B', fontSize: 12 }}
                  axisLine={{ stroke: '#CBD5E1' }}
                  tickFormatter={(value) => value.toFixed(1)}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fill: '#64748B', fontSize: 12 }}
                  axisLine={{ stroke: '#CBD5E1' }}
                  width={70}
                />
                <RechartsTooltip content={<CustomTooltip />} />
                <ReferenceLine x={0} stroke="#94A3B8" strokeWidth={2} />
                <Bar dataKey="tendencyScore" name="Tendency Score" radius={[0, 4, 4, 0]}>
                  {significantTendencies.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={getBarColor(entry.tendencyScore)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: '#3B82F6' }} />
              <Typography variant="caption" color="text.secondary">
                Tends to vote lower
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: '#10B981' }} />
              <Typography variant="caption" color="text.secondary">
                Balanced
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: '#EF4444' }} />
              <Typography variant="caption" color="text.secondary">
                Tends to vote higher
              </Typography>
            </Box>
          </Box>
        </>
      ) : (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Need at least 3 votes per participant for meaningful analysis
          </Typography>
        </Box>
      )}

      {/* Detailed table */}
      <Paper sx={{ mt: 3, overflow: 'hidden' }}>
        <Box
          component="table"
          sx={{
            width: '100%',
            borderCollapse: 'collapse',
            '& th, & td': {
              p: 1.5,
              textAlign: 'left',
              borderBottom: '1px solid',
              borderColor: 'divider'
            },
            '& th': {
              bgcolor: '#141414',
              fontWeight: 600,
              fontSize: '0.875rem'
            },
            '& td': {
              fontSize: '0.875rem'
            }
          }}
        >
          <thead>
            <tr>
              <th>Participant</th>
              <th>Tendency</th>
              <th>Total Votes</th>
              <th>Exact Match</th>
              <th>Higher</th>
              <th>Lower</th>
            </tr>
          </thead>
          <tbody>
            {tendencies.map((tendency) => (
              <tr key={tendency.id}>
                <td>
                  <Typography variant="body2" fontWeight={500}>
                    {tendency.name}
                  </Typography>
                </td>
                <td>
                  <Chip
                    icon={getTendencyIcon(tendency.tendencyScore)}
                    label={getTendencyLabel(tendency.tendencyScore)}
                    size="small"
                    sx={{
                      bgcolor: tendency.tendencyScore > 0.2
                        ? '#FEE2E2'
                        : tendency.tendencyScore < -0.2
                        ? '#DBEAFE'
                        : '#D1FAE5',
                      color: tendency.tendencyScore > 0.2
                        ? '#991B1B'
                        : tendency.tendencyScore < -0.2
                        ? '#1E40AF'
                        : '#065F46'
                    }}
                  />
                </td>
                <td>{tendency.totalVotes}</td>
                <td>
                  <MuiTooltip title="Voted same as final estimate">
                    <span>{tendency.exactMatchRate}%</span>
                  </MuiTooltip>
                </td>
                <td style={{ color: '#EF4444' }}>{tendency.votedHigherRate}%</td>
                <td style={{ color: '#3B82F6' }}>{tendency.votedLowerRate}%</td>
              </tr>
            ))}
          </tbody>
        </Box>
      </Paper>
    </Box>
  );
};

export default IndividualTendencies;
